import * as tslib_1 from "tslib";
import React, { useState, useRef, useEffect, useMemo, Fragment } from 'react';
import useSWRMutation from 'swr/mutation';
import useSWR from 'swr';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'react-bootstrap';
import _ from 'lodash';
import styled from 'styled-components';
import { AllStates, AllYearsEmployed, Country, EmploymentType, EmploymentTypes, EntityAccountType, getGroupedCountryOptions, ResponseCodes, } from '@tradingblock/types';
import { ApiFactory, GetUserMarketDataEntitlementsUrl, SubmitMktDataUserProfileReviewUrl, UpdateAccountUrl, } from '@tradingblock/api';
import { SelectDropdownField, StepperField, TextboxField } from '../localize/fields';
import { ExternalLink, Loading } from '../common';
import { FormGroup, Tooltip } from '../basic';
import { firstSetOfMarketDataQuestionnaireBooleanFormFields, secondSetOfMarketDataQuestionnaireBooleanFormFields } from '../../constants';
export const MARKET_DATA_QUESTIONNAIRE_HEADER = 'Market Data User Profile Review';
export const SUBMIT_WARNING = 'By submitting this form, you agree that all questions answered above and information provided are truthful and accurate.';
export const ELECTRONIC_DATA_AGREEMENT_VALIDATION_MSG = 'You must open and review the Electronic Data Agreement.';
export const GENERIC_SUBMISSION_ERROR_MSG = 'Something went wrong submitting the form. Please try submitting again.';
export const CONFIRM_CHANGES_BUTTON_TEXT = 'Confirm Changes';
export const EDIT_BUTTON_TEXT = 'Edit';
export const TEMP_DELAYED_QUOTES_WARNING = 'Based on the information provided above, you qualify as professional market data user. Following submission of this form, you will be entitled for delayed quotes until your real-time request is approved.';
const StyledDescription = styled.div `
    ul li {
        list-style: disc;
        margin-left: 14px;
    }
    ul {
        margin-bottom: 25px;
    }
`;
export const MarketDataQuestionnaireDescription = () => (React.createElement(StyledDescription, null,
    React.createElement("p", null, "Please answer the questions below to verify your status as a professional or non-professional user of NYSE, AMEX, Nasdaq and OPRA market data. Please note:"),
    React.createElement("ul", null,
        React.createElement("li", null, "Delayed market data is provided at no cost to all users."),
        React.createElement("li", null, "Real-time market data is provided at no cost to all non-professional users."),
        React.createElement("li", null, "Real-time market data is provided at cost to all professional users."))));
const defaultInitialMarketDataQuestionnaireFormValues = {
    questions: {
        finraRelated: null,
        industryRelated: null,
        stakeHolder: null,
        govOfficial: null,
        foreignShellBank: null,
        personalUse: null,
        businessUse: null,
        secCftcRegistration: null,
        associationRegistration: null,
        jobFunctions: null,
        investmentProfessional: null,
        assetManager: null,
        capitalTrading: null,
        corporationTrading: null,
        profitSharing: null,
        benefitsReceived: null,
    },
    realtimeRequested: null,
    employmentType: null,
    employer: null,
    jobPosition: null,
    occupation: null,
    employmentFunctions: null,
    yearsEmployed: null,
    employerAddress: {
        country: null,
        address1: null,
        address2: null,
        city: null,
        state: null,
        postalCode: null,
    },
};
const sixthQuestionKey = Object.keys(firstSetOfMarketDataQuestionnaireBooleanFormFields)[5];
const createYesNoStepperFieldData = (isDisabled) => ([{ label: 'Yes', value: true, disabled: isDisabled }, { label: 'No', value: false, disabled: isDisabled }]);
const StyledForm = styled.div `
    ul li {
        list-style: disc;
        margin-left: 14px;
    }
    .error {
        display: inline-block;
        margin-top: 5px;
    }
    .submit-warning {
        font-weight: bold;
        text-align: left;
        margin-top: 25px;
    }
    .submit-btn-wrapper {
        display: flex;
        justify-content: flex-end;
        align-items: center;
    }
    .professional-user-fees {
        border: 1px solid black;
        border-radius: 5px;
        padding: 25px;
        margin: 25px 0 25px 0;
        background-color: rgb(236, 236, 236, 0.2);
    }
    .professional-user-fees span {
        font-weight: bold;
    }
    .professional-user-fees .professional-user-qualification {
        font-weight: bold;
    }
    .professional-user-agreements {
        margin-bottom: ${params => (params.useCustomBtnPositioning ? '1rem' : '25px')};
    }
    .professional-user-agreements a {
        display: inline-block;
    }
    ol li {
        list-style: decimal;
        margin-left: 17px;
    }
    .employment-data-form-groups {
        margin: 25px 0;
    }
    .form-error-msg {
        text-align: center;
        width: 100%;
    }
    .btn-primary:disabled {
        border-color: #ccc;
    }
    .btn-primary:disabled:hover {
        opacity: 0.65;
        background-color: #ccc;
    }
    .loading-container {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .temp-delayed-quotes-warning {
        font-weight: bold;
    }
    button.btn-dark:disabled:hover {
        border-color: unset;
    }
`;
export const MarketDataQuestionnaireForm = ({ apiToken, rootRestHubUrl, onSubmissionFailure, onSubmissionSuccess, useCustomBtnPositioning, useCustomErrorMsgPositioning, setSubmitBtnRef, setErrorMsg, setSubmitBtnDisabled, setSubmitBtnText, setShouldRefetchProfileData, setShouldRefetchAccountManagementDetailsData, setShowTempDelayedQuotesWarningWithCustomPositioning, accountId, accountIdOfAuthUserIsSameAsSelectedAccountId, }) => {
    const [showSecondSetOfBooleanFields, setShowSecondSetOfBooleanFields] = useState(null);
    const [isProfessionalUser, setIsProfessionalUser] = useState(null);
    const [isRequestingRealTimeQuotes, setIsRequestingRealTimeQuotes] = useState(null);
    const [formErrMsg, setFormErrMsg] = useState(null);
    const [hasClickedOnExchangeDataAgreementLink, setHasClickedOnExchangeDataAgreementLink] = useState(null);
    const [isEmployed, setIsEmployed] = useState(null);
    const [isCountryUs, setIsCountryUs] = useState(null);
    const [initialMarketDataQuestionnaireFormValues, setInitialMarketDataQuestionnaireFormValues] = useState(defaultInitialMarketDataQuestionnaireFormValues);
    const [previousFormDataSet, setPreviousFormDataSet] = useState(false);
    const [changesMadeToForm, setChangesMadeToForm] = useState(false);
    const [potentiallyPro, setPotentiallyPro] = useState(null);
    const [showElectronicDataAgreementValidationMsg, setShowElectronicDataAgreementValidationMsg] = useState(false);
    const [loadingAndValidatingCheckPassed, setLoadingAndValidatingCheckPassed] = useState(false);
    const [showTempDelayedQuotesWarning, setShowTempDelayedQuotesWarning] = useState(false);
    const [shouldDefaultBusinessUseQuestionToYesAndDisable, setShouldDefaultBusinessUseQuestionToYesAndDisable] = useState(false);
    const api = useMemo(() => ApiFactory(rootRestHubUrl, apiToken || ''), [apiToken]);
    const { trigger: triggerSubmitMktDataUserProfileReview, } = useSWRMutation(`${rootRestHubUrl}/${SubmitMktDataUserProfileReviewUrl}`, (_url, { arg }) => api.entitlements.submitMktDataUserProfileReview(arg.submitMktDataUserProfileReviewRequestPayload));
    const { trigger: triggerUpdateAccount, } = useSWRMutation(`${rootRestHubUrl}/${UpdateAccountUrl}`, (_url, { arg }) => api.accounts.update(accountId, arg.updateAccountEmploymentInfoRequestPayload));
    const { isLoading: isLoadingUserMarketDataEntitlementsData, data: getUserMarketDataEntitlementsResponse, error: errorGettingUserMarketDataEntitlementsData, isValidating: isValidatingUserMarketDataEntitlementsData, } = useSWR(`${rootRestHubUrl}/${GetUserMarketDataEntitlementsUrl}`, () => api.entitlements.getUserMarketDataEntitlements(), { revalidateOnFocus: false, shouldRetryOnError: false });
    const prevIsRequestingRealTimeQuotes = useRef(null);
    const electronicDataAgreementDivRef = useRef(null);
    const prevIsLoadingUserMarketDataEntitlementsData = useRef(isLoadingUserMarketDataEntitlementsData);
    const prevIsValidatingUserMarketDataEntitlementsData = useRef(isValidatingUserMarketDataEntitlementsData);
    useEffect(() => {
        if ((!isLoadingUserMarketDataEntitlementsData && prevIsLoadingUserMarketDataEntitlementsData.current) ||
            (!isValidatingUserMarketDataEntitlementsData && prevIsValidatingUserMarketDataEntitlementsData.current) ||
            (!isLoadingUserMarketDataEntitlementsData && prevIsLoadingUserMarketDataEntitlementsData.current && !isValidatingUserMarketDataEntitlementsData && prevIsValidatingUserMarketDataEntitlementsData.current)) {
            setLoadingAndValidatingCheckPassed(true);
        }
    }, [isLoadingUserMarketDataEntitlementsData, isValidatingUserMarketDataEntitlementsData, prevIsLoadingUserMarketDataEntitlementsData, prevIsValidatingUserMarketDataEntitlementsData]);
    useEffect(() => {
        prevIsValidatingUserMarketDataEntitlementsData.current = isValidatingUserMarketDataEntitlementsData;
    }, [isValidatingUserMarketDataEntitlementsData]);
    useEffect(() => {
        prevIsLoadingUserMarketDataEntitlementsData.current = isLoadingUserMarketDataEntitlementsData;
    }, [isLoadingUserMarketDataEntitlementsData]);
    useEffect(() => {
        if (getUserMarketDataEntitlementsResponse && !errorGettingUserMarketDataEntitlementsData && getUserMarketDataEntitlementsResponse.payload && loadingAndValidatingCheckPassed) {
            if (getUserMarketDataEntitlementsResponse.responseCode === 0) {
                const _a = getUserMarketDataEntitlementsResponse.payload, { entityAccountType, potentiallyPro } = _a, marketDataQuestionnaireFormValues = tslib_1.__rest(_a, ["entityAccountType", "potentiallyPro"]);
                let personalUse = null;
                let businessUse = null;
                // We are deleting the nonPersonalUse key because the backend still returns it but in order to match the form mapping we only want to keep the personalUse key.
                delete marketDataQuestionnaireFormValues.questions.nonPersonalUse;
                if (potentiallyPro) {
                    personalUse = false;
                }
                else {
                    if (marketDataQuestionnaireFormValues.questions.personalUse === false || marketDataQuestionnaireFormValues.questions.personalUse === true) {
                        personalUse = marketDataQuestionnaireFormValues.questions.personalUse;
                    }
                    else {
                        personalUse = defaultInitialMarketDataQuestionnaireFormValues.questions.personalUse;
                    }
                }
                if (entityAccountType && entityAccountType !== EntityAccountType.PersonalTrust) {
                    businessUse = true;
                    setShouldDefaultBusinessUseQuestionToYesAndDisable(true);
                }
                else {
                    if (marketDataQuestionnaireFormValues.questions.businessUse === false || marketDataQuestionnaireFormValues.questions.businessUse === true) {
                        businessUse = marketDataQuestionnaireFormValues.questions.businessUse;
                    }
                    else {
                        businessUse = defaultInitialMarketDataQuestionnaireFormValues.questions.businessUse;
                    }
                }
                setInitialMarketDataQuestionnaireFormValues(Object.assign({}, defaultInitialMarketDataQuestionnaireFormValues, marketDataQuestionnaireFormValues, { questions: Object.keys(marketDataQuestionnaireFormValues.questions).length === 0 ? Object.assign({}, defaultInitialMarketDataQuestionnaireFormValues.questions, { personalUse, businessUse }) : Object.assign({}, defaultInitialMarketDataQuestionnaireFormValues.questions, marketDataQuestionnaireFormValues.questions, { personalUse,
                        businessUse }) }, (marketDataQuestionnaireFormValues.employmentType !== EmploymentType.Employed && {
                    employerAddress: defaultInitialMarketDataQuestionnaireFormValues.employerAddress,
                    employer: defaultInitialMarketDataQuestionnaireFormValues.employer,
                    jobPosition: defaultInitialMarketDataQuestionnaireFormValues.jobPosition,
                    employmentFunctions: defaultInitialMarketDataQuestionnaireFormValues.employmentFunctions,
                    occupation: defaultInitialMarketDataQuestionnaireFormValues.occupation,
                    yearsEmployed: defaultInitialMarketDataQuestionnaireFormValues.yearsEmployed,
                })));
                setPreviousFormDataSet(true);
                setPotentiallyPro(potentiallyPro);
            }
        }
    }, [getUserMarketDataEntitlementsResponse, errorGettingUserMarketDataEntitlementsData, loadingAndValidatingCheckPassed]);
    useEffect(() => {
        prevIsRequestingRealTimeQuotes.current = isRequestingRealTimeQuotes;
    }, [isRequestingRealTimeQuotes]);
    useEffect(() => {
        if (hasClickedOnExchangeDataAgreementLink === false && isRequestingRealTimeQuotes) {
            setShowElectronicDataAgreementValidationMsg(true);
        }
        else {
            setShowElectronicDataAgreementValidationMsg(false);
        }
    }, [hasClickedOnExchangeDataAgreementLink, isRequestingRealTimeQuotes]);
    useEffect(() => {
        if (useCustomErrorMsgPositioning) {
            if (showElectronicDataAgreementValidationMsg) {
                setErrorMsg(ELECTRONIC_DATA_AGREEMENT_VALIDATION_MSG);
                return;
            }
            if (formErrMsg) {
                setErrorMsg(formErrMsg);
                return;
            }
            setErrorMsg('');
        }
    }, [formErrMsg, showElectronicDataAgreementValidationMsg]);
    useEffect(() => {
        if (isRequestingRealTimeQuotes && isProfessionalUser) {
            if (setShowTempDelayedQuotesWarningWithCustomPositioning) {
                setShowTempDelayedQuotesWarningWithCustomPositioning(true);
            }
            else {
                setShowTempDelayedQuotesWarning(true);
            }
        }
        else {
            if (setShowTempDelayedQuotesWarningWithCustomPositioning) {
                setShowTempDelayedQuotesWarningWithCustomPositioning(false);
            }
            else {
                setShowTempDelayedQuotesWarning(false);
            }
        }
    }, [isRequestingRealTimeQuotes, isProfessionalUser]);
    const onSubmit = async (values, { setSubmitting }) => {
        setFormErrMsg('');
        if (!hasClickedOnExchangeDataAgreementLink && isRequestingRealTimeQuotes) {
            electronicDataAgreementDivRef && electronicDataAgreementDivRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHasClickedOnExchangeDataAgreementLink(false);
            setSubmitting(false);
            return;
        }
        try {
            if (isRequestingRealTimeQuotes) {
                const { employerAddress, employer, jobPosition, employmentType, yearsEmployed, } = values;
                const cleanedEmploymentInfoData = {
                    employerAddress: !isEmployed ? null : Object.assign({}, employerAddress, { address1: employerAddress && employerAddress.address1 && employerAddress.address1.trim(), address2: employerAddress && employerAddress.address2 && employerAddress.address2.trim(), city: employerAddress && employerAddress.city && employerAddress.city.trim(), postalCode: employerAddress && employerAddress.postalCode && employerAddress.postalCode.trim(), state: employerAddress && employerAddress.state && employerAddress.state.trim() }),
                    employer: employer && employer.trim(),
                    jobPosition: jobPosition && jobPosition.trim(),
                    employmentType,
                    yearsEmployed,
                };
                const updateAccountEmploymentInfoRequestPayload = {
                    accountHolder: cleanedEmploymentInfoData,
                    holder: 0,
                    item: 'Employment',
                };
                const { responseCode: updateAccountEmploymentInfoResponseCode } = await triggerUpdateAccount({ updateAccountEmploymentInfoRequestPayload });
                if (updateAccountEmploymentInfoResponseCode !== 0) {
                    if (ResponseCodes[updateAccountEmploymentInfoResponseCode]) {
                        throw new Error(ResponseCodes[updateAccountEmploymentInfoResponseCode].description);
                    }
                    else {
                        throw new Error(GENERIC_SUBMISSION_ERROR_MSG);
                    }
                }
            }
            const { occupation, employmentFunctions, questions, realtimeRequested, } = values;
            const cleanedDataForSubmitMktDataUserProfileReviewRequest = {
                questions,
                realtimeRequested,
                occupation: occupation && occupation.trim(),
                employmentFunctions: employmentFunctions && employmentFunctions.trim(),
            };
            const { responseCode: submitMktDataUserProfileReviewResponseCode } = await triggerSubmitMktDataUserProfileReview({
                submitMktDataUserProfileReviewRequestPayload: cleanedDataForSubmitMktDataUserProfileReviewRequest,
            });
            if (submitMktDataUserProfileReviewResponseCode !== 0) {
                if (onSubmissionFailure) {
                    onSubmissionFailure(submitMktDataUserProfileReviewResponseCode);
                }
                if (ResponseCodes[submitMktDataUserProfileReviewResponseCode]) {
                    throw new Error(ResponseCodes[submitMktDataUserProfileReviewResponseCode].description);
                }
                else {
                    throw new Error(GENERIC_SUBMISSION_ERROR_MSG);
                }
            }
            if (submitMktDataUserProfileReviewResponseCode === 0) {
                setShouldRefetchProfileData && setShouldRefetchProfileData(true);
                if (accountIdOfAuthUserIsSameAsSelectedAccountId) {
                    setShouldRefetchAccountManagementDetailsData && setShouldRefetchAccountManagementDetailsData(true);
                }
                onSubmissionSuccess();
            }
        }
        catch (err) {
            let errorMessage = '';
            if (err instanceof Error && err.message && Object.keys(err).length === 1) {
                errorMessage = err.message;
            }
            else {
                errorMessage = GENERIC_SUBMISSION_ERROR_MSG;
            }
            setFormErrMsg(errorMessage);
            setSubmitting(false);
        }
    };
    const determineIfProfessionalUser = (values) => setIsProfessionalUser(!!Object.keys(firstSetOfMarketDataQuestionnaireBooleanFormFields).find((booleanFormField) => booleanFormField === sixthQuestionKey ? !values.questions[booleanFormField] : values.questions[booleanFormField]));
    const formFieldValidationSchema = Yup.string().nullable().trim().required('This field is required.');
    const address1And2FormFieldValidationSchema = Yup.string().matches(/^[\u0020-\u007E]*$/, { excludeEmptyString: true, message: 'Please make sure the address field above is valid.' }).max(30, 'This field must be no more than 30 characters, including spaces.');
    const postalCodeFormFieldValidationSchema = Yup.string().matches(/^\d{5}(?:[-]\d{4})?$/, { excludeEmptyString: true, message: 'Please make sure the postal code above is valid.' });
    const handleAgreementClick = () => {
        setHasClickedOnExchangeDataAgreementLink(true);
    };
    return (React.createElement(StyledForm, { useCustomBtnPositioning: useCustomBtnPositioning },
        React.createElement("div", { className: "market-data-questionnaire-form" },
            isLoadingUserMarketDataEntitlementsData && (React.createElement("div", { className: "loading-container" },
                React.createElement(Loading, null))),
            loadingAndValidatingCheckPassed && ((getUserMarketDataEntitlementsResponse && getUserMarketDataEntitlementsResponse.responseCode === 0 && previousFormDataSet) ||
                !getUserMarketDataEntitlementsResponse ||
                getUserMarketDataEntitlementsResponse.responseCode !== 0 ||
                errorGettingUserMarketDataEntitlementsData) &&
                React.createElement(Formik, { validateOnBlur: true, validateOnChange: true, validationSchema: Yup.object().shape(Object.assign({ realtimeRequested: formFieldValidationSchema, questions: Yup.object().shape(Object.assign({ finraRelated: formFieldValidationSchema, industryRelated: formFieldValidationSchema, stakeHolder: formFieldValidationSchema, govOfficial: formFieldValidationSchema, foreignShellBank: formFieldValidationSchema, personalUse: formFieldValidationSchema }, (showSecondSetOfBooleanFields && {
                            businessUse: formFieldValidationSchema,
                            associationRegistration: formFieldValidationSchema,
                            jobFunctions: formFieldValidationSchema,
                            investmentProfessional: formFieldValidationSchema,
                            secCftcRegistration: formFieldValidationSchema,
                            capitalTrading: formFieldValidationSchema,
                            corporationTrading: formFieldValidationSchema,
                            profitSharing: formFieldValidationSchema,
                            benefitsReceived: formFieldValidationSchema,
                            assetManager: formFieldValidationSchema,
                        }))) }, (isRequestingRealTimeQuotes && Object.assign({ employmentType: formFieldValidationSchema }, (isEmployed && {
                        employerAddress: Yup.object().shape(Object.assign({ address1: formFieldValidationSchema.concat(address1And2FormFieldValidationSchema), address2: Yup.string().nullable().trim().concat(address1And2FormFieldValidationSchema), city: formFieldValidationSchema, country: formFieldValidationSchema }, ((isCountryUs || isCountryUs === null) && {
                            postalCode: formFieldValidationSchema.concat(postalCodeFormFieldValidationSchema),
                            state: formFieldValidationSchema,
                        }))),
                        employmentFunctions: formFieldValidationSchema,
                        jobPosition: formFieldValidationSchema,
                        occupation: formFieldValidationSchema,
                        employer: formFieldValidationSchema,
                        yearsEmployed: formFieldValidationSchema,
                    }))))), initialValues: initialMarketDataQuestionnaireFormValues, onSubmit: onSubmit }, ({ values, isSubmitting, resetForm, errors, isValid, isValidating }) => {
                    useEffect(() => {
                        if (!isValid && !isValidating && isSubmitting) {
                            let inputName = '';
                            Object.keys(defaultInitialMarketDataQuestionnaireFormValues).find((formInputKey) => {
                                const rootErrorKey = Object.keys(errors).find((errorKey) => errorKey === formInputKey);
                                if (rootErrorKey) {
                                    if (typeof errors[rootErrorKey] === 'object') {
                                        inputName = Object.keys(defaultInitialMarketDataQuestionnaireFormValues[rootErrorKey]).find((formInputKey) => !!Object.keys(errors[rootErrorKey]).find((errorKey) => errorKey === formInputKey));
                                        return true;
                                    }
                                    inputName = formInputKey;
                                    return true;
                                }
                                return false;
                            });
                            if (inputName) {
                                if (Object.keys(defaultInitialMarketDataQuestionnaireFormValues.questions).includes(inputName)) {
                                    inputName = `questions.${inputName}`;
                                }
                                if (Object.keys(defaultInitialMarketDataQuestionnaireFormValues.employerAddress).includes(inputName)) {
                                    inputName = `employerAddress.${inputName}`;
                                }
                                const elementToScrollTo = document.querySelector(`input[id='${inputName}']`) || document.getElementById(`btn-${inputName}-0`);
                                if (elementToScrollTo) {
                                    elementToScrollTo.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                            }
                        }
                    }, [errors, isValid, isValidating, isSubmitting]);
                    // This section will cleanup the form fields if they are hidden and set back to the null value.
                    // Or if the field is visible the value of the field will be set to the form value or the initial form data state value.
                    useEffect(() => {
                        const getBooleanFieldValue = (booleanFormFieldKey) => {
                            if (values.questions[booleanFormFieldKey] === false || values.questions[booleanFormFieldKey] === true) {
                                return values.questions[booleanFormFieldKey];
                            }
                            if (initialMarketDataQuestionnaireFormValues.questions[booleanFormFieldKey] === false || initialMarketDataQuestionnaireFormValues.questions[booleanFormFieldKey] === true) {
                                return initialMarketDataQuestionnaireFormValues.questions[booleanFormFieldKey];
                            }
                            return null;
                        };
                        resetForm({
                            values: Object.assign({}, values, { questions: Object.assign({}, values.questions, (showSecondSetOfBooleanFields !== null && Object.keys(secondSetOfMarketDataQuestionnaireBooleanFormFields).reduce((accumulator, booleanFormFieldKey) => (Object.assign({}, accumulator, { [booleanFormFieldKey]: showSecondSetOfBooleanFields ? getBooleanFieldValue(booleanFormFieldKey) : defaultInitialMarketDataQuestionnaireFormValues.questions[booleanFormFieldKey] })), {}))) }, (isRequestingRealTimeQuotes !== null && { employmentType: isRequestingRealTimeQuotes ? (values.employmentType || initialMarketDataQuestionnaireFormValues.employmentType) : defaultInitialMarketDataQuestionnaireFormValues.employmentType }), (isEmployed !== null && {
                                employer: isEmployed ? (values.employer || initialMarketDataQuestionnaireFormValues.employer) : defaultInitialMarketDataQuestionnaireFormValues.employer,
                                occupation: isEmployed ? (values.occupation || initialMarketDataQuestionnaireFormValues.occupation) : defaultInitialMarketDataQuestionnaireFormValues.occupation,
                                employmentFunctions: isEmployed ? (values.employmentFunctions || initialMarketDataQuestionnaireFormValues.employmentFunctions) : defaultInitialMarketDataQuestionnaireFormValues.employmentFunctions,
                                jobPosition: isEmployed ? (values.jobPosition || initialMarketDataQuestionnaireFormValues.jobPosition) : defaultInitialMarketDataQuestionnaireFormValues.jobPosition,
                                yearsEmployed: isEmployed ? (values.yearsEmployed || initialMarketDataQuestionnaireFormValues.yearsEmployed) : defaultInitialMarketDataQuestionnaireFormValues.yearsEmployed,
                                employerAddress: isEmployed ? ((values.employerAddress && !!Object.keys(values.employerAddress).find((employerAddressKey) => values.employerAddress && !!values.employerAddress[employerAddressKey]) && values.employerAddress) || initialMarketDataQuestionnaireFormValues.employerAddress) : defaultInitialMarketDataQuestionnaireFormValues.employerAddress,
                            }))
                        });
                    }, [showSecondSetOfBooleanFields, isEmployed, isRequestingRealTimeQuotes, initialMarketDataQuestionnaireFormValues]);
                    useEffect(() => setShowSecondSetOfBooleanFields(!values.questions[sixthQuestionKey]), [values.questions[sixthQuestionKey]]);
                    useEffect(() => determineIfProfessionalUser(values), [
                        values.questions.finraRelated,
                        values.questions.industryRelated,
                        values.questions.stakeHolder,
                        values.questions.govOfficial,
                        values.questions.foreignShellBank,
                        values.questions.personalUse,
                    ]);
                    useEffect(() => setIsRequestingRealTimeQuotes(values.realtimeRequested), [values.realtimeRequested]);
                    useEffect(() => setIsEmployed(values.employmentType === EmploymentType.Employed), [values.employmentType]);
                    useEffect(() => {
                        // This will reset if they have viewed the exchange data agreement if the isRequestingRealTimeQuotes state changes.
                        if ((!isRequestingRealTimeQuotes && prevIsRequestingRealTimeQuotes) ||
                            (isRequestingRealTimeQuotes && !prevIsRequestingRealTimeQuotes)) {
                            setHasClickedOnExchangeDataAgreementLink(null);
                        }
                    }, [isRequestingRealTimeQuotes]);
                    useEffect(() => {
                        if (!values.employerAddress || (values.employerAddress && !values.employerAddress.country)) {
                            setIsCountryUs(null);
                        }
                        else if (values.employerAddress && values.employerAddress.country === Country.UnitedStatesOfAmerica) {
                            setIsCountryUs(true);
                        }
                        else if (values.employerAddress && values.employerAddress.country !== Country.UnitedStatesOfAmerica) {
                            setIsCountryUs(false);
                        }
                    }, [values.employerAddress && values.employerAddress.country]);
                    // Whenever there is a change in the form data do a check to see
                    // if the data is different than the initial form data.
                    // This will dictate the form button text.
                    useEffect(() => {
                        const getBooleanFieldValue = (booleanFormFieldKey) => {
                            if (initialMarketDataQuestionnaireFormValues.questions[booleanFormFieldKey] === true || initialMarketDataQuestionnaireFormValues.questions[booleanFormFieldKey] === false) {
                                return initialMarketDataQuestionnaireFormValues.questions[booleanFormFieldKey];
                            }
                            return null;
                        };
                        const modifiedInitialMarketDataQuestionnaireFormValues = Object.assign({}, initialMarketDataQuestionnaireFormValues, { questions: Object.assign({}, initialMarketDataQuestionnaireFormValues.questions, (showSecondSetOfBooleanFields !== null && Object.keys(secondSetOfMarketDataQuestionnaireBooleanFormFields).reduce((accumulator, booleanFormFieldKey) => (Object.assign({}, accumulator, { [booleanFormFieldKey]: showSecondSetOfBooleanFields ? getBooleanFieldValue(booleanFormFieldKey) : defaultInitialMarketDataQuestionnaireFormValues.questions[booleanFormFieldKey] })), {}))) }, (isRequestingRealTimeQuotes !== null && { employmentType: isRequestingRealTimeQuotes ? initialMarketDataQuestionnaireFormValues.employmentType : defaultInitialMarketDataQuestionnaireFormValues.employmentType }), (isEmployed !== null && {
                            employer: isEmployed ? initialMarketDataQuestionnaireFormValues.employer : defaultInitialMarketDataQuestionnaireFormValues.employer,
                            occupation: isEmployed ? initialMarketDataQuestionnaireFormValues.occupation : defaultInitialMarketDataQuestionnaireFormValues.occupation,
                            employmentFunctions: isEmployed ? initialMarketDataQuestionnaireFormValues.employmentFunctions : defaultInitialMarketDataQuestionnaireFormValues.employmentFunctions,
                            jobPosition: isEmployed ? initialMarketDataQuestionnaireFormValues.jobPosition : defaultInitialMarketDataQuestionnaireFormValues.jobPosition,
                            yearsEmployed: isEmployed ? initialMarketDataQuestionnaireFormValues.yearsEmployed : defaultInitialMarketDataQuestionnaireFormValues.yearsEmployed,
                            employerAddress: isEmployed ?
                                (initialMarketDataQuestionnaireFormValues.employerAddress &&
                                    !!Object.keys(initialMarketDataQuestionnaireFormValues.employerAddress)
                                        .find((employerAddressKey) => initialMarketDataQuestionnaireFormValues.employerAddress &&
                                        !!initialMarketDataQuestionnaireFormValues.employerAddress[employerAddressKey]))
                                    && initialMarketDataQuestionnaireFormValues.employerAddress :
                                defaultInitialMarketDataQuestionnaireFormValues.employerAddress,
                        }));
                        if (!_.isEqual(modifiedInitialMarketDataQuestionnaireFormValues, values) && !changesMadeToForm) {
                            setChangesMadeToForm(true);
                        }
                        else if (_.isEqual(modifiedInitialMarketDataQuestionnaireFormValues, values) && changesMadeToForm) {
                            setChangesMadeToForm(false);
                        }
                    }, [values, initialMarketDataQuestionnaireFormValues]);
                    const submitButtonText = useMemo(() => {
                        if (changesMadeToForm) {
                            return CONFIRM_CHANGES_BUTTON_TEXT;
                        }
                        if (useCustomBtnPositioning) {
                            if (isSubmitting) {
                                return 'Editing';
                            }
                            return EDIT_BUTTON_TEXT;
                        }
                        if (isSubmitting) {
                            return 'Submitting';
                        }
                        return 'Submit';
                    }, [changesMadeToForm, useCustomBtnPositioning, isSubmitting]);
                    return (React.createElement(Form, { translate: undefined },
                        Object.keys(firstSetOfMarketDataQuestionnaireBooleanFormFields).map((booleanFormFieldKey) => (React.createElement(Fragment, { key: booleanFormFieldKey },
                            (booleanFormFieldKey !== sixthQuestionKey || (booleanFormFieldKey === sixthQuestionKey && !potentiallyPro)) &&
                                React.createElement(FormGroup, null,
                                    React.createElement("label", { htmlFor: `questions.${booleanFormFieldKey}` }, firstSetOfMarketDataQuestionnaireBooleanFormFields[booleanFormFieldKey]),
                                    React.createElement(Field, { id: `questions.${booleanFormFieldKey}`, name: `questions.${booleanFormFieldKey}`, component: StepperField, options: createYesNoStepperFieldData(isSubmitting) })),
                            booleanFormFieldKey === sixthQuestionKey && potentiallyPro &&
                                React.createElement(FormGroup, null,
                                    React.createElement(Tooltip, { effect: "solid", place: "top", content: 'It has been determined you are a potentially pro user. Because of this the answer to this question must be "No".', id: booleanFormFieldKey },
                                        React.createElement("label", { htmlFor: `questions.${booleanFormFieldKey}` }, firstSetOfMarketDataQuestionnaireBooleanFormFields[booleanFormFieldKey]),
                                        React.createElement(Field, { id: `questions.${booleanFormFieldKey}`, name: `questions.${booleanFormFieldKey}`, component: StepperField, options: createYesNoStepperFieldData(true) })))))),
                        showSecondSetOfBooleanFields && (React.createElement(React.Fragment, null, Object.keys(secondSetOfMarketDataQuestionnaireBooleanFormFields).map((booleanFormFieldKey) => (React.createElement(FormGroup, { key: booleanFormFieldKey },
                            React.createElement("label", { htmlFor: booleanFormFieldKey }, secondSetOfMarketDataQuestionnaireBooleanFormFields[booleanFormFieldKey]),
                            React.createElement(Field, { id: `questions.${booleanFormFieldKey}`, name: `questions.${booleanFormFieldKey}`, component: StepperField, options: createYesNoStepperFieldData(booleanFormFieldKey === Object.keys(secondSetOfMarketDataQuestionnaireBooleanFormFields)[0] && shouldDefaultBusinessUseQuestionToYesAndDisable ? true : isSubmitting) })))))),
                        React.createElement(FormGroup, { className: "quotes" },
                            React.createElement("label", { htmlFor: "realtimeRequested" }, "Select whether you wish to receive Real-time or Delayed quotes:"),
                            React.createElement(Field, { id: "realtimeRequested", name: "realtimeRequested", component: StepperField, options: [{ label: 'Delayed quotes (Free to all users)', value: false, disabled: isSubmitting }, { label: 'Real-time quotes (Free to non-pro users)', value: true, disabled: isSubmitting }] })),
                        isProfessionalUser && isRequestingRealTimeQuotes && (React.createElement("div", { className: "professional-user-fees" },
                            React.createElement("p", { className: "professional-user-qualification" }, "Based on the preceding information you supplied as part of this account application, you or the entity applying for this brokerage account qualifies as a \"Professional User\" as defined by the NYSE, AMEX, NASDAQ and the US options exchanges."),
                            React.createElement("p", null, "Exchanges define a \"Professional User\" as an individual or entity that meets any one of the following qualifications:"),
                            React.createElement("ol", null,
                                React.createElement("li", null, "An employee or a member of any stock exchange or of any business registered under any securities law or regulation; or"),
                                React.createElement("li", null, "An individual or entity trading in the account as a paid agent for a third party; or"),
                                React.createElement("li", null, "An individual or entity whose account is in the name of a corporation, partnership or sole proprietorship.")),
                            React.createElement("p", null,
                                React.createElement("span", null, "Professional User Data Fees May Apply."),
                                " Professional Users must choose to receive real-time or 15-minute delayed market quotes. Delayed market quotes are provided free of charge. Professional Users selecting real-time market quotes will be charged the following monthly data exchange fees:"),
                            React.createElement("ul", null,
                                React.createElement("li", null, "NYSE - $45.00"),
                                React.createElement("li", null, "AMEX - $23.00"),
                                React.createElement("li", null, "NASDAQ/UTP - $24.00"),
                                React.createElement("li", null, "OPRA - $31.50")),
                            React.createElement("p", null, "All fees and applicable taxes for real-time market quotes are charged in U.S. Dollars and are automatically deducted from your brokerage account each month and remitted to the exchange on your behalf. Fees are subject to change on a minimum of 60 days notice."))),
                        isRequestingRealTimeQuotes && (React.createElement("div", { className: "employment-data-form-groups" },
                            React.createElement("h4", null, "Employment Information"),
                            React.createElement(FormGroup, null,
                                React.createElement(Field, { component: SelectDropdownField, isSearchable: false, id: "employmentType", name: "employmentType", options: EmploymentTypes, label: "Employment Status", disabled: isSubmitting })),
                            isEmployed && (React.createElement(React.Fragment, null,
                                React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: TextboxField, id: "employer", name: "employer", label: "Employer Name", disabled: isSubmitting })),
                                React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: TextboxField, id: "jobPosition", name: "jobPosition", label: "Job Title", disabled: isSubmitting })),
                                React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: TextboxField, id: "occupation", name: "occupation", label: "Employment Occupation", disabled: isSubmitting })),
                                React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: TextboxField, id: "employmentFunctions", name: "employmentFunctions", label: "Employment Function", disabled: isSubmitting })),
                                React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: SelectDropdownField, isSearchable: false, id: "yearsEmployed", name: "yearsEmployed", options: AllYearsEmployed, label: "Years Employed", disabled: isSubmitting })),
                                React.createElement("h5", null, "Employer Address"),
                                React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: SelectDropdownField, name: "employerAddress.country", id: "employerAddress.country", label: "Country", options: getGroupedCountryOptions(), optionsDefaultLabelId: "country", disabled: isSubmitting })),
                                React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: TextboxField, name: "employerAddress.address1", id: "employerAddress.address1", label: "Address 1", disabled: isSubmitting })),
                                React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: TextboxField, name: "employerAddress.address2", id: "employerAddress.address2", label: "Address 2", disabled: isSubmitting })),
                                React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: TextboxField, name: "employerAddress.city", id: "employerAddress.city", label: "City", disabled: isSubmitting })),
                                (isCountryUs || isCountryUs === null) && (React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: SelectDropdownField, name: "employerAddress.state", id: "employerAddress.state", label: "State", options: AllStates, optionsDefaultLabelId: "state", disabled: isSubmitting }))),
                                !isCountryUs && isCountryUs !== null && (React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: TextboxField, name: "employerAddress.state", id: "employerAddress.state", label: "Province", disabled: isSubmitting }))),
                                React.createElement(FormGroup, null,
                                    React.createElement(Field, { component: TextboxField, name: "employerAddress.postalCode", id: "employerAddress.postalCode", label: "Postal Code", disabled: isSubmitting })))))),
                        isRequestingRealTimeQuotes && (React.createElement("div", { className: "professional-user-agreements", ref: electronicDataAgreementDivRef },
                            React.createElement("p", null, "By selecting real-time quotes, you are required to review the Exchange Data Agreement listed below. Please click on the agreement title below to open the agreement in a new tab."),
                            React.createElement("p", null, "By submitting this form, you agree that you have received, read and understand the Exchange Data Agreement, and you agree to all the provisions in the document."),
                            React.createElement(ExternalLink, { href: `${rootRestHubUrl}/Documents/Agreement/ExchangeDataAgreement`, onClick: handleAgreementClick, className: "exchange-data-agreement-link" }, "Exchange Data Agreement"))),
                        !useCustomBtnPositioning && React.createElement("p", { className: "submit-warning" }, SUBMIT_WARNING),
                        !useCustomBtnPositioning && showTempDelayedQuotesWarning && React.createElement("p", { className: "temp-delayed-quotes-warning mt-0" }, TEMP_DELAYED_QUOTES_WARNING),
                        formErrMsg && !useCustomErrorMsgPositioning && React.createElement("p", { className: "error form-error-msg" }, formErrMsg),
                        showElectronicDataAgreementValidationMsg && !useCustomErrorMsgPositioning &&
                            React.createElement("p", { className: "error form-error-msg" }, ELECTRONIC_DATA_AGREEMENT_VALIDATION_MSG),
                        !useCustomBtnPositioning ?
                            React.createElement("div", { className: "submit-btn-wrapper" },
                                React.createElement(Button, { variant: "primary", type: "submit", disabled: isSubmitting }, submitButtonText))
                            :
                                React.createElement("button", { type: "submit", disabled: isSubmitting, style: { display: 'none' }, ref: (node) => {
                                        setSubmitBtnDisabled(!!(node && node.disabled));
                                        setSubmitBtnText(submitButtonText);
                                        setSubmitBtnRef(node);
                                    } })));
                }))));
};
//# sourceMappingURL=MarketDataQuestionnaireForm.js.map